import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { attachmentError } from '@/lib/attachments';
import { useToast } from '@/components/ui';
import { CARDS, COLUMNS, FILES, type SampleAttachment } from './model';
import { OperationsContext } from './operationsContext';

export function OperationsProvider({ children }: { children: ReactNode }) {
  const [files, setFiles] = useState<SampleAttachment[]>(FILES);
  const [cards, setCards] = useState(CARDS);
  const sources = useRef(new Map<string, File>());
  const urls = useRef(new Set<string>());
  const timers = useRef(new Map<string, number>());
  const { toast } = useToast();
  useEffect(() => {
    const liveUrls = urls.current,
      liveTimers = timers.current;
    return () => {
      liveUrls.forEach((url) => URL.revokeObjectURL(url));
      liveTimers.forEach(window.clearTimeout);
    };
  }, []);
  const start = useCallback((id: string, fail: boolean) => {
    const source = sources.current.get(id);
    if (!source) return;
    const invalid = attachmentError(source, 25 * 1024 ** 2);
    if (invalid) {
      setFiles((all) => all.map((f) => (f.id === id ? { ...f, state: 'error', error: invalid } : f)));
      return;
    }
    window.clearTimeout(timers.current.get(id));
    setFiles((all) =>
      all.map((f) => (f.id === id ? { ...f, state: 'uploading', progress: 0, error: undefined } : f)),
    );
    let step = 0;
    const tick = () => {
      const progress = [25, 65, 100][step++];
      setFiles((all) =>
        all.map((f) =>
          f.id !== id
            ? f
            : fail && progress === 65
              ? {
                  ...f,
                  state: 'error',
                  progress,
                  error: 'Не удалось завершить добавление. Попробуй ещё раз.',
                }
              : { ...f, progress, state: progress === 100 ? 'ready' : 'uploading' },
        ),
      );
      if (progress < 100 && !(fail && progress === 65)) timers.current.set(id, window.setTimeout(tick, 600));
      else timers.current.delete(id);
    };
    timers.current.set(id, window.setTimeout(tick, 600));
  }, []);
  const addFiles = useCallback(
    (incoming: File[], area: 'card' | 'comment', fail: boolean) => {
      const additions = incoming.map((source) => {
        const id = crypto.randomUUID();
        sources.current.set(id, source);
        const url = URL.createObjectURL(source);
        urls.current.add(url);
        return {
          id,
          name: source.name,
          size: source.size,
          mime: source.type,
          url,
          state: 'uploading' as const,
          progress: 0,
          area,
        };
      });
      setFiles((all) => [...all, ...additions]);
      additions.forEach((f, i) => start(f.id, fail && i === 0));
    },
    [start],
  );
  const move = (id: string, columnId: string) => {
    const before = cards.find((c) => c.id === id);
    if (!before || before.columnId === columnId || !COLUMNS.some((c) => c.id === columnId)) return;
    setCards((all) => all.map((c) => (c.id === id ? { ...c, columnId } : c)));
    toast({
      title: 'Карточка перенесена: ' + COLUMNS.find((c) => c.id === columnId)?.title,
      action: {
        label: 'Отменить',
        undo: true,
        onClick: () =>
          setCards((all) => all.map((c) => (c.id === id ? { ...c, columnId: before.columnId } : c))),
      },
    });
  };
  return (
    <OperationsContext.Provider value={{ files, cards, addFiles, retry: (id) => start(id, false), move }}>
      {children}
    </OperationsContext.Provider>
  );
}
